import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { Reference } from 'sr-types/lib/workflow/v1/graphql';
import type { EntityName } from '../../auth/Security';
import { FormProvider } from '../../form/FormContext';
import Validator, { ValidatorType } from '../../form/Validator';
import { failedToastMessage, successToastMessage } from '../../utils/commonUtils';
import { APPROVAL_STRATEGIES, ApprovalDataInvalidError } from '../constants';
import { useSaveApprovalInputApi } from '../data/useSaveApprovalApi';
import { ApprovalInputViewModel, CreateApprovalContextValue } from '../types';
import ApprovalInputValidityObserver from './components/ApprovalInputValidityObserver';
import { CreateApprovalContext } from './components/CreateApprovalContext';

const emptyApprovalInput: ApprovalInputViewModel = {
    entityReference: { id: '', type: '' },
    summary: '',
    attachments: [],
    approverContacts: [],
    notifyContacts: [],
    approvalStrategy: APPROVAL_STRATEGIES.allApprovalsRequired
};

const approvalInputValidationRules: Record<
    Extract<keyof ApprovalInputViewModel, 'entityReference' | 'summary' | 'approverContacts' | 'approvalStrategy'>,
    Array<ValidatorType>
> = {
    entityReference: [Validator.RULES.isRequired],
    summary: [Validator.RULES.isRequired],
    approverContacts: [Validator.RULES.isRequired],
    approvalStrategy: [Validator.RULES.isRequired]
};

export type CreateApprovalProviderProps = PropsWithChildren<{
    /**
     * The entity reference for which the approval is being created.
     */
    entityReference: Reference;
    entity: EntityName;

    /**
     * Optional flag to indicate whether the data operation function exposed by this context provider
     * should handle success/error scenarios and show basic UI notifications,
     * or return result / throw errors to be handled by the caller.
     *
     * @default true
     */
    throwOnError?: boolean;
}>;

/**
 * @todo:
 *
 * 1. Add the `entityReference: Reference` to be a parameter of `saveApprovalInput` function,
 * instead of being a prop of the provider. Modify form validation rules to account for this.
 * Modify saveApprovalInput to handle this lazy entity reference scenarios.
 */
export function CreateApprovalProvider({
    entityReference,
    entity,
    throwOnError = true,
    children
}: CreateApprovalProviderProps) {
    const initialApprovalInput = useMemo<ApprovalInputViewModel>(() => {
        return {
            ...emptyApprovalInput,
            entityReference: entityReference ?? emptyApprovalInput.entityReference
        };
    }, [entityReference]);

    const [approvalInput, setApprovalInput] = useState<ApprovalInputViewModel>(initialApprovalInput);

    const [isApprovalInputValid, setIsApprovalInputValid] = useState<boolean>(false);

    const { saveApprovalInputApi, isSavingApprovalInput } = useSaveApprovalInputApi();

    const saveApprovalInput = useCallback<CreateApprovalContextValue['saveApprovalInput']>(
        async (initiate) => {
            let result: Awaited<ReturnType<typeof saveApprovalInputApi>> = null;

            if (throwOnError) {
                if (!isApprovalInputValid) {
                    throw new ApprovalDataInvalidError();
                }

                result = await saveApprovalInputApi(approvalInput, initiate);
            } else {
                if (!isApprovalInputValid) {
                    failedToastMessage('approval.data.invalid.message');
                    return null;
                }

                try {
                    result = await saveApprovalInputApi(approvalInput, initiate);
                    successToastMessage('approval.save.success.message');
                } catch (error) {
                    failedToastMessage('approval.save.failed.message');
                    return null;
                }
            }

            setApprovalInput(initialApprovalInput);
            return result;
        },
        [approvalInput, initialApprovalInput, isApprovalInputValid, saveApprovalInputApi, throwOnError]
    );

    const contextValue = useMemo(() => {
        const contextValue: CreateApprovalContextValue = {
            entityReference,
            entity,
            approvalInput,
            isApprovalInputValid,
            saveApprovalInput,
            isSavingApprovalInput
        };

        return contextValue;
    }, [approvalInput, entity, entityReference, isApprovalInputValid, isSavingApprovalInput, saveApprovalInput]);

    return (
        <CreateApprovalContext value={contextValue}>
            <FormProvider
                state={approvalInput}
                setState={setApprovalInput}
                validationRules={approvalInputValidationRules}
            >
                {children}
                <ApprovalInputValidityObserver onApprovalInputValidityChange={setIsApprovalInputValid} />
            </FormProvider>
        </CreateApprovalContext>
    );
}
